/**
 * The Page Order sequence editor (task 071-03, README D13). Chelsie's
 * 2026-10-01 ask: move the custom pages inside a sequence without a chain of
 * single-spot form saves around taken spots.
 *
 * It lists a sequence's pages that can still run, in API order, as a sortable
 * list (drag handle, keyboard, or Move up / Move down), and saves the new
 * order in one `PUT /admin/page-order/sequences/:sequence`. Saving numbers the
 * pages 1…K and takes the ended pages out of the sequence. The server decides
 * which pages are ended; a disagreement is a 409, never a bad write.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type ScreenReaderInstructions,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { api, ApiError, type PageOrderResponse, type PageOrderRow } from '../lib/api';
import { Button } from '../components/ui/button';
import { StatusPill } from '../components/ui/status-pill';
import { useToast } from '../components/ui/toast';
import { cn } from '../lib/utils';

/**
 * A completed or archived page has ended: no walk shows it, and a save takes
 * it out of the sequence. Mirrors the api's README D9 rule; the server decides.
 */
export function isEndedPage(row: PageOrderRow): boolean {
  return row.status === 'completed' || row.archived;
}

const INSTRUCTIONS: ScreenReaderInstructions = {
  draggable:
    'To pick up a page, focus its handle and press Space or Enter. Use the Up and Down arrow keys to move it, Space or Enter to drop it, or Escape to cancel. The Move up and Move down buttons move a page one step.',
};

interface SequenceReorderProps {
  sequence: number;
  /** The sequence's rows in API order, ended pages included. */
  rows: PageOrderRow[];
  onSaved(data: PageOrderResponse): void;
  onStale(): void;
  onCancel(): void;
}

interface SortableRowProps {
  row: PageOrderRow;
  position: number;
  count: number;
  disabled: boolean;
  onMove(from: number, to: number): void;
}

function SortableRow({ row, position, count, disabled, onMove }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.campaignId, disabled });
  const index = position - 1;
  const name = row.campaignName;

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2',
        isDragging && 'relative z-10 shadow-md'
      )}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Drag ${name}`}
        disabled={disabled}
        className="cursor-grab touch-none rounded px-1 text-lg leading-none text-muted hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
      >
        ⠿
      </button>
      <span className="w-6 text-right font-semibold tabular-nums">{position}</span>
      <span className="min-w-0 flex-1 font-semibold text-ink">{name}</span>
      <StatusPill value={row.status} />
      <span className="text-sm text-muted-foreground">
        In the walk today: {row.liveToday ? 'Yes' : 'No'}
      </span>
      <span className="w-16 text-sm tabular-nums text-muted-foreground">
        {row.spot !== null ? `Spot ${row.spot}` : 'No spot'}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={`Move ${name} up`}
        disabled={disabled || index === 0}
        onClick={() => onMove(index, index - 1)}
      >
        ↑
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={`Move ${name} down`}
        disabled={disabled || index === count - 1}
        onClick={() => onMove(index, index + 1)}
      >
        ↓
      </Button>
    </li>
  );
}

export function SequenceReorder({
  sequence,
  rows,
  onSaved,
  onStale,
  onCancel,
}: SequenceReorderProps) {
  const toast = useToast();
  const runnable = useMemo(() => rows.filter((row) => !isEndedPage(row)), [rows]);
  const ended = useMemo(() => rows.filter(isEndedPage), [rows]);
  const [draft, setDraft] = useState<PageOrderRow[]>(runnable);
  const [saving, setSaving] = useState(false);

  // README D13 (manager ruling 2026-10-01): Save order is enabled only when the order differs.
  const orderChanged = draft.some((row, index) => row.campaignId !== runnable[index].campaignId);

  useEffect(() => {
    if (!orderChanged) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [orderChanged]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const announcements = useMemo<Announcements>(() => {
    const count = draft.length;
    const positionOf = (id: UniqueIdentifier) =>
      draft.findIndex((row) => row.campaignId === String(id)) + 1;
    const nameOf = (id: UniqueIdentifier) =>
      draft.find((row) => row.campaignId === String(id))?.campaignName ?? String(id);
    return {
      onDragStart: ({ active }) =>
        `Picked up ${nameOf(active.id)}, at position ${positionOf(active.id)} of ${count}.`,
      onDragOver: ({ active, over }) =>
        over
          ? `${nameOf(active.id)} moved to position ${positionOf(over.id)} of ${count}.`
          : `${nameOf(active.id)} is not over a position.`,
      onDragEnd: ({ active, over }) =>
        over
          ? `Dropped ${nameOf(active.id)} at position ${positionOf(over.id)} of ${count}.`
          : `Dropped ${nameOf(active.id)}. It stays at position ${positionOf(active.id)} of ${count}.`,
      onDragCancel: ({ active }) =>
        `Cancelled. ${nameOf(active.id)} stays at position ${positionOf(active.id)} of ${count}.`,
    };
  }, [draft]);

  function move(from: number, to: number) {
    setDraft((current) => arrayMove(current, from, to));
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || over.id === active.id) return;
    setDraft((current) => {
      const from = current.findIndex((row) => row.campaignId === String(active.id));
      const to = current.findIndex((row) => row.campaignId === String(over.id));
      return from < 0 || to < 0 ? current : arrayMove(current, from, to);
    });
  }

  async function save() {
    setSaving(true);
    let response: PageOrderResponse;
    try {
      response = await api.reorderPageSequence(
        sequence,
        draft.map((row) => ({ campaignId: row.campaignId, expectedSpot: row.spot }))
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(`${err.message} The list now shows the current order.`);
        onStale();
        return;
      }
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Couldn't save the order: ${message}`);
      setSaving(false);
      return;
    }
    toast.success(`Saved the order of Sequence ${sequence}.`);
    onSaved(response);
  }

  return (
    <div>
      <p className="text-sm text-ink">
        Drag a page by its handle, or use the arrows, into the order visitors should see. Saving
        numbers the pages 1, 2, 3… in this order.
      </p>
      {ended.length > 0 && (
        <p className="mt-1 text-sm text-muted-foreground">
          Saving also takes these ended pages out of this sequence, so they stop holding spots:{' '}
          {ended.map((row) => row.campaignName).join(', ')}. If you reopen one, it joins the end of
          the walk again.
        </p>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
        accessibility={{ announcements, screenReaderInstructions: INSTRUCTIONS }}
      >
        <SortableContext
          items={draft.map((row) => row.campaignId)}
          strategy={verticalListSortingStrategy}
        >
          <ol aria-label={`Order of Sequence ${sequence}`} className="mt-4 flex flex-col gap-2">
            {draft.map((row, index) => (
              <SortableRow
                key={row.campaignId}
                row={row}
                position={index + 1}
                count={draft.length}
                disabled={saving}
                onMove={move}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>

      <div className="mt-4 flex items-center justify-end gap-3">
        {orderChanged && <span className="text-sm font-semibold text-amber">Unsaved changes</span>}
        <Button type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={save} disabled={!orderChanged || saving}>
          {saving ? 'Saving…' : 'Save order'}
        </Button>
      </div>
    </div>
  );
}
